import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!token) toast.error("Brak tokenu resetującego");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Hasło musi mieć co najmniej 8 znaków"); return; }
    if (password !== confirmPwd) { toast.error("Hasła nie są identyczne"); return; }
    setLoading(true);
    try {
      await publicApi.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Hasło zmienione");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Błąd resetu");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {done ? (
          <div className="brut bg-white rounded-md p-8 text-center" data-testid="reset-success">
            <div className="brut bg-[#D1FAE5] w-16 h-16 rounded-md flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8" strokeWidth={2.5} />
            </div>
            <h1 className="font-display font-black text-2xl mb-3">Hasło zmienione</h1>
            <p className="text-[#334155] mb-6">Możesz teraz zalogować się nowym hasłem.</p>
            <Button onClick={() => nav("/teacher/login")} className="w-full brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md">
              Przejdź do logowania
            </Button>
          </div>
        ) : !token ? (
          <div className="brut bg-white rounded-md p-8 text-center" data-testid="reset-no-token">
            <div className="brut bg-[#FEE2E2] w-16 h-16 rounded-md flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8" strokeWidth={2.5} />
            </div>
            <h1 className="font-display font-black text-2xl mb-3">Nieprawidłowy link</h1>
            <p className="text-[#334155] mb-4">Token resetujący jest niedostępny. Wyślij nowy link.</p>
            <Link to="/forgot-password">
              <Button className="w-full brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md">
                Wyślij nowy link
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display font-black text-4xl mb-2">Nowe hasło</h1>
            <p className="text-[#334155] mb-8">Ustaw nowe hasło do swojego konta.</p>
            <form onSubmit={submit} className="brut bg-white rounded-md p-6 space-y-4">
              <div>
                <Label className="font-bold text-sm">Nowe hasło (min. 8 znaków)</Label>
                <PasswordInput
                  id="reset-password"
                  value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
                  className="mt-2 brut-input border-2 border-[#0F172A] rounded-md h-12 font-mono px-3"
                  placeholder="••••••••" autoFocus data-testid="reset-password-input"
                />
              </div>
              <div>
                <Label className="font-bold text-sm">Powtórz nowe hasło</Label>
                <PasswordInput
                  id="reset-confirm-password"
                  value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required minLength={8}
                  className={`mt-2 brut-input border-2 rounded-md h-12 font-mono px-3 ${
                    confirmPwd && password !== confirmPwd ? "border-[#EF4444]" : "border-[#0F172A]"
                  }`}
                  placeholder="••••••••" data-testid="reset-confirm-password-input"
                />
                {confirmPwd && password !== confirmPwd && (
                  <div className="text-xs text-[#EF4444] mt-1 font-bold" data-testid="reset-password-mismatch">
                    Hasła nie są identyczne
                  </div>
                )}
              </div>
              <Button type="submit" disabled={loading || password !== confirmPwd || password.length < 8} className="w-full brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md h-12" data-testid="reset-submit-btn">
                {loading ? "Zmieniam..." : "Ustaw nowe hasło"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
